// Node-specific JavaScript functionality

// Build Resource node detail elements
export function initNodeElements() {
  const nodeElement = document.querySelector('[data-history-node-id]');
  if (!nodeElement) return;

  const nodeId = nodeElement.getAttribute('data-history-node-id');
  if (!nodeId) return;

  const badge = document.getElementById('data-grades');
  if (!badge) return;

  const aligned = document.getElementById('data-aligned');
  const resourceTypes = document.getElementById('data-resource-types');

  try {
    fetchResourceData(nodeId, badge, aligned, resourceTypes);
  } catch (error) {
    console.error('Failed to fetch grade levels:', error);
  }
}

async function fetchResourceData(nodeId, badge, aligned, resourceTypes) {
  const response = await fetch(`/api/v1/nodes/resource?id=${nodeId}`);
  if (!response.ok) throw new Error(`API error: ${response.status}`);

  const payload = await response.json();
  const item = Array.isArray(payload) ? payload?.[0] : payload;
  badge.textContent = summarizeGrades(item?.grade_levels) || 'Grade -';

  if (!aligned) return;
  const isAligned = item?.is_aligned === 'True';
  if (isAligned) {
    aligned.textContent = 'Ontario Curriculum Aligned';
    aligned.classList.remove('d-none', 'hidden');
  } else {
    aligned.classList.add('d-none', 'hidden');
  }

  if (!resourceTypes) return;
  const rawTypes = item?.resource_types;
  const types = Array.isArray(rawTypes)
    ? rawTypes.map(String)
    : typeof rawTypes === 'string'
      ? rawTypes.split(',').map((s) => s.trim()).filter(Boolean)
      : [];

  if (types.length) {
    const badges = types
      .map(
        (t) =>
          `<span class="badge bg-dark bg-opacity-10 border-1 border-camerise-teal fw-semibold px-3 py-2 rounded-pill text-dark">${escapeHtml(t)}</span>`,
      )
      .join('');
    resourceTypes.innerHTML =
      `<div class="fw-bold mb-2 text-gray">Resource Type</div>` +
      `<div class="d-flex flex-wrap gap-2">${badges}</div>`;
    resourceTypes.classList.remove('d-none', 'hidden');
  } else {
    resourceTypes.classList.add('d-none', 'hidden');
  }

  if (isAligned && item?.selected_curriculums) {
    buildCurriculumAlignment(item.selected_curriculums, item?.programs ?? '').catch((err) =>
      console.error('Failed to build curriculum alignment:', err),
    );
  }
}

// ---------------------------------------------------------------------------
// Curriculum alignment
// ---------------------------------------------------------------------------

// Share the same localStorage cache key as curriculum-api.js so the taxonomy
// is fetched at most once per 24 hours regardless of which script runs first.
const TAXONOMY_CACHE_KEY = 'curriculumTaxonomyCache';
const CACHE_TTL = 24 * 60 * 60 * 1000;

// Root node names that bracket the curriculum subjects.
const ROOT_NAMES = new Set(['Elementary (K-12)', 'Secondary (9-12)']);

async function loadTaxonomy() {
  try {
    const stored = localStorage.getItem(TAXONOMY_CACHE_KEY);
    if (stored) {
      const { data, timestamp } = JSON.parse(stored);
      if (Date.now() - timestamp < CACHE_TTL) return data;
      localStorage.removeItem(TAXONOMY_CACHE_KEY);
    }
  } catch (e) { /* ignore parse/quota errors */ }

  const res = await fetch('/api/v2/taxonomy/ontario-curriculum');
  if (!res.ok) throw new Error(`Taxonomy API error: ${res.status}`);
  const data = await res.json();

  try {
    localStorage.setItem(TAXONOMY_CACHE_KEY, JSON.stringify({ data, timestamp: Date.now() }));
  } catch (e) { /* ignore quota errors */ }

  return data;
}

/**
 * The taxonomy API returns a flat list of Drupal term objects.
 * Normalize to a plain byTid map: { [tid]: { tid, name, parentTid } }
 */
function normalizeTaxonomy(rawItems) {
  const byTid = {};
  for (const item of rawItems) {
    const tid = String(item.tid?.[0]?.value ?? '');
    if (!tid) continue;
    const name = item.name?.[0]?.value ?? '';
    const shortcode = item.field_shortcode?.[0]?.value ?? '';
    byTid[tid] = {
      tid,
      name,
      label: shortcode || name,
      parentTid: item.parent?.[0]?.target_id ? String(item.parent[0].target_id) : null,
    };
  }
  return byTid;
}

/**
 * Walk the parent chain upward from startTid.
 * Return the first ancestor whose parent is a root node (Elementary / Secondary).
 * That ancestor IS the curriculum subject (e.g. "French as a Second Language (2013)").
 */
function findSubjectAncestor(startTid, byTid, rootTids) {
  let tid = startTid;
  while (tid && byTid[tid]) {
    const term = byTid[tid];
    if (term.parentTid && rootTids.has(term.parentTid)) {
      return term;
    }
    tid = term.parentTid;
  }
  return null;
}

async function buildCurriculumAlignment(selectedCurriculums, programs = '') {
  // Use the anchor div from the Twig template; if the DS block is not placed,
  // fall back to appending directly inside the node article element.
  let section = document.getElementById('curriculum-alignment-section');
  if (!section) {
    const article = document.querySelector('[data-history-node-id]');
    if (!article) return;
    section = document.createElement('div');
    section.id = 'curriculum-alignment-section';
    article.appendChild(section);
  }

  const rawItems = await loadTaxonomy();
  const byTid = normalizeTaxonomy(rawItems);

  // Collect TIDs of the two root nodes by matching their names.
  const rootTids = new Set(
    Object.values(byTid)
      .filter((t) => ROOT_NAMES.has(t.name))
      .map((t) => t.tid),
  );

  // Group selected term IDs by their curriculum subject.
  const selectedIds = String(selectedCurriculums)
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);

  const groups = {};
  const subjectLabels = {};
  const order = [];

  for (const id of selectedIds) {
    const term = byTid[id];
    if (!term) continue;
    const subject = findSubjectAncestor(id, byTid, rootTids);
    if (!subject) continue;
    const sid = subject.tid;
    if (!groups[sid]) {
      groups[sid] = [];
      subjectLabels[sid] = subject.label;
      order.push(sid);
    }
    groups[sid].push(term.label);
  }

  if (!order.length) return;

  const rows = order
    .map((sid) => {
      const s = escapeHtml(subjectLabels[sid]);
      const standards = groups[sid].map(escapeHtml).join(', ');
      return `<tr class="border-top">
          <td class="py-3 px-4">${s}</td>
          <td class="py-3 px-4 text-muted">${standards}</td>
        </tr>`;
    })
    .join('');

  const programBadges = programs
    ? programs
        .split(',')
        .map((p) => p.trim())
        .filter(Boolean)
        .map(
          (p) =>
            `<span class="badge rounded-pill border border-camerise-teal text-black bg-camerise-teal bg-opacity-10 fw-semibold px-3 py-2">${escapeHtml(p)}</span>`,
        )
        .join('')
    : '';

  section.innerHTML = `
    <section class="mt-4">
      <h2 class="h5 fw-bold mb-3">Ontario Curriculum Alignment</h2>
      ${programBadges ? `<div class="d-flex flex-wrap gap-2 mb-3">${programBadges}</div>` : ''}
      <div class="border border-camerise-teal rounded-3 overflow-hidden">
        <table class="table table-borderless mb-0">
          <thead class="bg-camerise-teal bg-opacity-10">
            <tr>
              <th class="fw-bold text-dark py-3 px-4">Curriculum</th>
              <th class="fw-bold text-dark py-3 px-4">Aligned Standards</th>
            </tr>
          </thead>
          <tbody>${rows}</tbody>
        </table>
      </div>
    </section>`;

  section.classList.remove('d-none');
}

function escapeHtml(str) {
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

// Summarise grade labels ("Kindergarten, Grade 1, Grade 2") the same way as
// the server-side grade_summary Twig filter (GradeLevels::summarize()):
// "All grades", "Elementary (K–8)", "Grades K–2, 6", "Not grade specific".
export function summarizeGrades(gradeText) {
  if (typeof gradeText !== 'string' || !gradeText.trim()) return '';

  const labels = gradeText.split(',').map((s) => s.trim()).filter(Boolean);
  const ordinals = [];
  for (const label of labels) {
    if (/^kindergarten$/i.test(label)) {
      ordinals.push(0);
      continue;
    }
    // Resources word the "No specific grade" term as "Not grade specific".
    if (/^no specific grade$/i.test(label)) return 'Not grade specific';
    const match = label.match(/^grade\s+(\d+)$/i);
    // Anything else is shown as is.
    if (!match) return label;
    ordinals.push(Number(match[1]));
  }

  const sorted = [...new Set(ordinals)].sort((a, b) => a - b);
  const isRange = (from, to) => sorted.length === to - from + 1 && sorted[0] === from && sorted[sorted.length - 1] === to;
  if (isRange(0, 12)) return 'All grades';
  if (isRange(0, 8)) return 'Elementary (K–8)';
  if (isRange(9, 12)) return 'Secondary (9–12)';
  if (sorted.length === 1) return labels[0];

  const format = (n) => (n === 0 ? 'K' : String(n));
  const ranges = [];
  let start = sorted[0];
  let prev = sorted[0];
  for (const n of [...sorted.slice(1), null]) {
    if (n !== null && n === prev + 1) {
      prev = n;
      continue;
    }
    ranges.push(start === prev ? format(start) : `${format(start)}–${format(prev)}`);
    start = n;
    prev = n;
  }
  return `Grades ${ranges.join(', ')}`;
}
